import { FC, memo, ReactNode, useContext } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineLineChart } from "react-icons/ai";
import { BiLineChartDown } from "react-icons/bi";
import { MdOutlineStackedLineChart } from "react-icons/md";
import {
  Card,
  CardSizesEnum,
  ScrollableContainer,
  ServiceIcons,
  StyledPageWrapper,
  Title,
  ViewportContext,
} from "@monorepo/components";
import { useClientQuery } from "@monorepo/services/api/auth";
import { useGetHistoryByOrderIdQuery, useGetHistoryByServiceIdQuery } from "@monorepo/services/api/history";
import theme, { iconsSizes } from "@monorepo/styles/theme";
import { IClient } from "@monorepo/types";
import { Person } from "@mui/icons-material";
import { Tooltip, Typography } from "@mui/material";
import { Box } from "@mui/system";

enum CardLayoutEnum {
  BIG = "big",
  DEFAULT = "default",
}

interface IProps {
  cardLayout?: CardLayoutEnum;
  icon: ReactNode;
  value: string;
  label: string;
  sublabel: string;
  client?: IClient | null;
}

const EntryCard: FC<IProps> = memo(({ cardLayout, icon, value, label, sublabel, client }) => {
  const { isMobile } = useContext(ViewportContext);
  const { t } = useTranslation();

  if (!isMobile && cardLayout === CardLayoutEnum.BIG) {
    return (
      <Card size={CardSizesEnum.SMALL} unclickable>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ position: "relative" }}
          flexDirection={{ xs: "row", md: "column" }}
        >
          <Box sx={{ position: { xs: "relative", sm: "absolute" }, left: 0, top: 0 }}>{icon}</Box>
          <Typography
            sx={{ ml: { md: theme.spacing(4), lg: 0 } }}
            color={theme.palette.text.primary}
            fontSize="0.825rem"
          >
            {label}
          </Typography>
          <Typography fontWeight="bold" color={theme.palette.text.primary} my={theme.spacing(2)}>
            {value}
          </Typography>
          <Typography fontSize="0.725rem">{sublabel}</Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      key={`${sublabel}-${label}-${value}`}
      size={CardSizesEnum.SMALL}
      sx={{
        display: "grid",
        gridTemplateColumns: client ? "1fr 14fr 1fr 3fr" : "1fr 15fr 3fr",
        gridGap: theme.spacing(2),
      }}
      unclickable
    >
      {icon}
      <Box>
        <Typography component="p">{label}</Typography>
        <Typography component="p">{sublabel}</Typography>
      </Box>
      {client && (
        <Tooltip
          title={t("payment_history.service_paid_by", { name: client.name })}
          sx={{ justifySelf: "flex-end", cursor: "pointer" }}
        >
          <Person />
        </Tooltip>
      )}
      <Typography textAlign="right">{value}</Typography>
    </Card>
  );
});

const HistoryServiceGroup = ({
  orderId,
  serviceId,
  cartGroupId,
}: {
  orderId: number;
  serviceId: number;
  cartGroupId: number;
}) => {
  const { t } = useTranslation();
  const { data: orderHistory } = useGetHistoryByOrderIdQuery(orderId);
  const { data: client } = useClientQuery();
  const { data: serviceHistory } = useGetHistoryByServiceIdQuery(
    {
      cart_group_id: cartGroupId,
      service_id: serviceId,
      customer_number: orderHistory?.customer_number?.map((cn) => cn.parameter_value) || [],
    },
    { skip: !orderHistory?.customer_number }
  );

  if (!serviceHistory || !orderHistory) return null;

  return (
    <StyledPageWrapper>
      <Title>{serviceHistory.order_name}</Title>
      <Typography variant="h6" textAlign="center">
        {serviceHistory.total} €
      </Typography>
      <ScrollableContainer>
        <Box
          display="grid"
          gridTemplateColumns="1fr"
          sx={{
            gap: theme.spacing(1),
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr 1fr",
              },
              gap: {
                xs: theme.spacing(1),
                md: theme.spacing(2),
              },
              marginBottom: {
                xs: 0,
                md: theme.spacing(3),
              },
            }}
          >
            <EntryCard
              cardLayout={CardLayoutEnum.BIG}
              icon={<AiOutlineLineChart fontSize={iconsSizes.large} color={theme.palette.primary.main} />}
              value={`${serviceHistory.biggest} €`}
              label={t("payment_history.biggest_payment")}
              sublabel={serviceHistory.biggest_date}
            />
            <EntryCard
              cardLayout={CardLayoutEnum.BIG}
              icon={<BiLineChartDown fontSize={iconsSizes.large} color={theme.palette.primary.main} />}
              value={`${serviceHistory.lowest} €`}
              label={t("payment_history.lowest_payment")}
              sublabel={serviceHistory.lowest_date}
            />
            <EntryCard
              cardLayout={CardLayoutEnum.BIG}
              icon={<MdOutlineStackedLineChart fontSize={iconsSizes.large} color={theme.palette.primary.main} />}
              value={`${serviceHistory.average} €`}
              label={t("payment_history.average_payment")}
              sublabel={`Per ${new Date().getFullYear()} m.`}
            />
          </Box>

          {serviceHistory.payments.map((p) => (
            <EntryCard
              icon={<ServiceIcons serviceId={orderHistory.category_id} />}
              value={`${p.amount} €`}
              label={p.service_name}
              sublabel={p.date}
              key={p.service_name}
              client={client?.id !== p.client?.id.toString() ? p.client : null}
            />
          ))}
        </Box>
      </ScrollableContainer>
    </StyledPageWrapper>
  );
};
export default HistoryServiceGroup;
